import { Fragment, useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { AlertCircle, Maximize2, Minimize2, RefreshCw } from "lucide-react";
import { PaginationControls } from "@/admin/components/paginated-table";
import { Button } from "@/shared/components/ui/button";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { HighlightMatch } from "@/shared/components/ui/highlight-match";
import { SortIndicator, type SortDirection } from "./sort-indicator";
import { visibleOrderedColumns } from "./visible-columns";
import { cn } from "@/shared/lib/utils";
import {
  formatDisplayValue,
  type DisplayFormatOptions,
  type FormFieldMeta,
} from "@/shared/lib/forms";
import { useFormattingContext } from "@/shared/lib/formatting";

// ─── Types ─────────────────────────────────────────────────

export interface SortingState {
  field: string;
  direction: "asc" | "desc";
}

export interface DataTableColumn<T> {
  /** Unique column identifier */
  id: string;
  /** Display header label */
  header: string;
  /** Key on the row data object for simple access */
  accessorKey?: keyof T;
  /**
   * Raw-value extractor for nested/EAV shapes (e.g. pulling a value out
   * of `row.attribute_values`). When `field` is also set and `cell` is
   * not, the returned value is formatted by `formatDisplayValue`.
   */
  getValue?: (row: T) => unknown;
  /**
   * Field metadata. When provided and no `cell` is given, the cell is
   * rendered by `formatDisplayValue(field, value, formatOptions)` so
   * dates, money, files, selects, booleans, etc. render correctly per
   * their field type. Value source: `getValue` if set, else `accessorKey`.
   */
  field?: FormFieldMeta;
  /** Custom cell renderer - receives the full row. Wins over `field`. */
  cell?: (row: T) => ReactNode;
  /**
   * Pin this column to the right edge so it stays reachable however far the
   * table is scrolled. For a wide table the row actions would otherwise sit
   * beyond the viewport - a user has to scroll a long way sideways to find the
   * edit button, which is a needless barrier and worst for the people least
   * able to absorb it. Opt-in: no existing column changes unless it asks.
   */
  sticky?: "right";
  /** Right-align the header and cells, with tabular figures - for numbers. */
  align?: "right";
  /** Whether this column supports server-side sorting */
  sortable?: boolean;
  /** Backend field name sent as `ordering` param (defaults to id) */
  sortField?: string;
  /**
   * What the column holds, so a sorted header can say its order in words
   * ("Newest first", "A to Z") instead of leaving people to decode an arrow.
   */
  sortKind?: "date" | "text" | "number";
  /** Whether this column is visible by default (defaults true) */
  defaultVisible?: boolean;
  /** Filter metadata for the filter bar */
  meta?: {
    filterType?: "text" | "select" | "date";
    filterField?: string;
    filterOptions?: Array<{ value: string; label: string }>;
  };
}

interface DataTableProps<T> {
  /** Unique table identifier for view persistence */
  tableId: string;
  /** Column definitions */
  columns: DataTableColumn<T>[];
  /** Row data */
  data: T[];
  /** Total number of rows (for pagination). Omit to hide pagination. */
  totalCount?: number;
  /** Loading state - true on the first fetch when there is no data yet. */
  isLoading?: boolean;
  /**
   * Background-fetch state (React Query `isFetching`). Pass this so the table
   * can tell "settled, genuinely empty" apart from "a refetch is in flight and
   * we have no rows yet". The latter must render the loading row, NOT the empty
   * state - otherwise a stale/empty persisted cache flashes "No results" while
   * the real data is still loading. When rows are already present, `isFetching`
   * is ignored (painted rows are never replaced by a spinner on background
   * refetch), per the caching doctrine in CLAUDE.md.
   */
  isFetching?: boolean;
  /**
   * Error state. When true (and there is no data to fall back on), the table
   * renders an error row with a Retry action instead of the empty state - so a
   * failed fetch never masquerades as "no results". Pair with `onRetry` to let
   * the user recover in place (no full-page reload needed).
   */
  isError?: boolean;
  /** Called when the user clicks Retry in the error state (e.g. `refetch`). */
  onRetry?: () => void;
  /** Current page (1-indexed) */
  page?: number;
  /** Page size */
  pageSize?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  /** Current sort state */
  sorting?: SortingState | null;
  onSortingChange?: (sorting: SortingState | null) => void;
  /** Row click handler */
  onRowClick?: (row: T) => void;
  /** Custom empty state message */
  emptyMessage?: string;
  /** Custom empty state JSX (takes precedence over emptyMessage) */
  emptyStateNode?: ReactNode;
  /** Row key accessor - defaults to 'uuid' */
  rowKey?: keyof T | ((row: T) => string);
  /** Column visibility - keys are column ids */
  columnVisibility?: Record<string, boolean>;
  /** Column order - array of column ids determining display order */
  columnOrder?: string[];
  /** Custom row renderer for complex rows (e.g. expandable products) */
  renderRow?: (row: T, visibleColumns: DataTableColumn<T>[]) => ReactNode;
  /**
   * Render an inline detail row directly beneath a row (e.g. an
   * expandable line-item panel). Return `null`/`undefined` for rows that
   * are not expanded. Applies to the default row renderer only.
   */
  renderRowDetail?: (
    row: T,
    visibleColumns: DataTableColumn<T>[],
  ) => ReactNode;
  /**
   * Number of leading columns to leave empty before the detail content
   * cell, so the detail aligns natively with a given column (e.g. start
   * at the description column). Defaults to 0 (full-width colSpan).
   */
  renderRowDetailStartColumn?: number;
  /**
   * When true, the detail row's backing (the cell behind the expanded
   * detail content, including any leading indent columns) is given a
   * darkened background so the nested content reads as sitting on a
   * highlighted surface. Opt-in so other detail-row consumers keep their
   * default styling.
   */
  highlightExpandedDetail?: boolean;
  /** Generate a data-testid for each row */
  rowTestId?: (row: T) => string;
  /**
   * Locale + currency used by `formatDisplayValue` for any column that
   * declares a `field`. Defaults to the surrounding `FormattingContext`
   * (org locale/currency) so callers rarely need to pass this.
   */
  formatOptions?: DisplayFormatOptions;
  /**
   * When set, the current search text. Any plain-text cell value (columns
   * rendered from `field`/`getValue`/`accessorKey`, not a custom `cell`) has
   * the matching tokens wrapped in <mark> so operators can see exactly what
   * matched what they typed - WPS-48. Custom `cell` renderers are left
   * untouched (they own their own markup, e.g. badges).
   */
  highlightQuery?: string;
  className?: string;
  /**
   * Constrain the table body to a max height and scroll vertically inside it
   * (a Tailwind max-height class, e.g. `max-h-[420px]`). The sticky header
   * stays pinned while the rows scroll - use for embedded surfaces like
   * dashboard tiles where the dataset can be large but the card must stay a
   * tidy fixed size. Omit on full list pages (they scroll with the page).
   */
  maxHeightClass?: string;
  /**
   * Compact density for embedded surfaces (dashboard tiles): smaller body
   * font (`--text-sm` vs `--text-base`) and tighter row/header/empty padding,
   * so a card stays small at rest and grows as rows fill in. Off by default so
   * the full list pages keep their comfortable, accessible density.
   */
  dense?: boolean;
  /**
   * Hide the built-in fullscreen toggle. For embedded surfaces (dashboard tiles)
   * whose frame already provides its own expand control, so the table does not
   * render a second, redundant expand affordance over its header.
   */
  hideFullscreen?: boolean;
  /**
   * Drop the table's own border, radius and background, for a table that sits
   * inside a card which already draws them.
   */
  flush?: boolean;
  /**
   * Keep an expanded row's detail panel inside the visible width instead of
   * letting it set the table's width.
   *
   * A detail panel is usually a long run of label/value pairs. It lives in a
   * `colSpan` cell, so in a `min-w-max` table its natural single-line width
   * becomes the table's minimum, and auto-layout then shares that excess out
   * across the real columns - a Qty column holding "20" ends up 148px wide and
   * the whole table needs 2.2x the viewport. Constraining the panel to the
   * visible width lets it wrap, and the columns collapse back to the width
   * their own content needs.
   */
  fitRowDetailToViewport?: boolean;
  /**
   * Enable a leading multi-select column: a per-row checkbox plus a select-all
   * checkbox in the header. Selection is controlled - pass `selectedKeys` and
   * `onSelectedKeysChange`. Select-all toggles the rows currently rendered (the
   * current page). Reusable across list pages that need bulk actions; the page
   * reads `selectedKeys` to render its own bulk-action bar.
   */
  selectable?: boolean;
  /** Controlled set of selected row keys (see `rowKey`). */
  selectedKeys?: Set<string>;
  /** Called with the next selection when a row / select-all toggles. */
  onSelectedKeysChange?: (keys: Set<string>) => void;
}

// ─── Component ─────────────────────────────────────────────

// Stable references for the undefined-prop fallback, so defaulting does not
// churn a new array each render (data feeds the scroll-overflow effect deps).
const EMPTY_ARRAY: never[] = [];

export function DataTable<T>({
  columns = EMPTY_ARRAY,
  data = EMPTY_ARRAY,
  totalCount,
  isLoading,
  isFetching,
  isError,
  onRetry,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  sorting,
  onSortingChange,
  onRowClick,
  emptyMessage,
  emptyStateNode,
  rowKey = "uuid" as keyof T,
  columnVisibility,
  columnOrder,
  renderRow,
  renderRowDetail,
  renderRowDetailStartColumn,
  highlightExpandedDetail,
  rowTestId,
  tableId,
  formatOptions,
  highlightQuery,
  className,
  maxHeightClass,
  dense,
  hideFullscreen,
  flush = false,
  fitRowDetailToViewport,
  selectable,
  selectedKeys,
  onSelectedKeysChange,
}: DataTableProps<T>) {
  // Density-aware cell / header / empty classes. Dense = smaller font +
  // tighter padding for embedded tiles; default = comfortable list-page density.
  const cellClass = dense
    ? "cv-data-table-small px-4 py-2 text-[var(--ink)]"
    : "cv-data-table-body px-4 py-3 text-[var(--ink)]";
  const headerPadClass = dense ? "px-4 py-2" : "px-4 py-3";
  const emptyClass = dense
    ? "cv-data-table-small px-4 py-6 text-center text-[var(--ink-3)]"
    : "cv-data-table-body px-4 py-10 text-center text-[var(--ink-3)]";
  const { t } = useTranslation();
  const formatting = useFormattingContext();
  const resolvedFormatOptions: DisplayFormatOptions = formatOptions ?? {
    locale: formatting.locale,
    currency: formatting.currency,
  };

  // Horizontal-overflow affordance: track whether the table is scrolled away
  // from each edge so the fade overlays only appear when there is more content
  // to reveal, and disappear once you reach an edge.
  const scrollRef = useRef<HTMLDivElement>(null);
  // Visible (not scrollable) width of the table viewport, published as a CSS
  // variable so a constrained row-detail panel can size against it. Measured
  // rather than assumed because the container is inside a responsive shell and
  // changes with the sidebar, fullscreen, and window resizes.
  const [viewportWidth, setViewportWidth] = useState<number | null>(null);
  useEffect(() => {
    if (!fitRowDetailToViewport) return;
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const measure = () => setViewportWidth(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [fitRowDetailToViewport]);
  const [overflow, setOverflow] = useState({ left: false, right: false });

  // Fullscreen: lifts the table out of the page's max-width shell and onto a
  // fixed overlay, so wide grids can be read without horizontal scrolling. Kept
  // entirely inside DataTable - no page wiring, no persistence (a fullscreen
  // view is a momentary "let me look at this", not a saved preference).
  const [isFullscreen, setFullscreen] = useState(false);

  // Escape exits, and the page behind the overlay must not scroll under it.
  useEffect(() => {
    if (!isFullscreen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setFullscreen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isFullscreen]);

  const visibleColumns = visibleOrderedColumns(columns, columnVisibility, columnOrder);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      const maxScroll = scrollWidth - clientWidth;
      const left = scrollLeft > 1;
      const right = scrollLeft < maxScroll - 1;
      setOverflow((prev) =>
        prev.left === left && prev.right === right ? prev : { left, right },
      );
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => {
      el.removeEventListener("scroll", update);
      observer.disconnect();
    };
    // isFullscreen is a dependency because toggling it swaps the table
    // between inline rendering and a body portal, which remounts scrollRef's
    // node - the listener + ResizeObserver must re-attach to the new element.
  }, [data, visibleColumns.length, isFullscreen]);

  function getRowKey(row: T, index: number): string {
    if (typeof rowKey === "function") return rowKey(row);
    const val = row[rowKey as keyof T];
    return val != null ? String(val) : String(index);
  }

  function handleSort(col: DataTableColumn<T>) {
    if (!col.sortable || !onSortingChange) return;

    const field = col.sortField ?? col.id;
    if (!sorting || sorting.field !== field) {
      onSortingChange({ field, direction: "asc" });
    } else if (sorting.direction === "asc") {
      onSortingChange({ field, direction: "desc" });
    } else {
      onSortingChange(null);
    }
  }

  // The order a sort direction means for a column, in words.
  function sortOrderLabel(
    kind: DataTableColumn<T>["sortKind"],
    direction: "asc" | "desc",
  ): string | null {
    if (kind === "date")
      return direction === "asc"
        ? t("dataTable.sort.oldestFirst", "Oldest first")
        : t("dataTable.sort.newestFirst", "Newest first");
    if (kind === "text")
      return direction === "asc"
        ? t("dataTable.sort.aToZ", "A to Z")
        : t("dataTable.sort.zToA", "Z to A");
    if (kind === "number")
      return direction === "asc"
        ? t("dataTable.sort.lowToHigh", "Low to high")
        : t("dataTable.sort.highToLow", "High to low");
    return null;
  }

  // What the next click on a sortable header does, for its tooltip.
  function sortHint(col: DataTableColumn<T>, direction: SortDirection): string {
    if (direction === "desc")
      return t("dataTable.sort.clickToClear", "Click to clear the sort");
    const next = sortOrderLabel(col.sortKind, direction === "asc" ? "desc" : "asc");
    if (next) return t("dataTable.sort.clickToSortOrder", "Click to sort: {{order}}", { order: next });
    return direction === "asc"
      ? t("dataTable.sort.clickToReverse", "Click to reverse the order")
      : t("dataTable.sort.clickToSort", "Click to sort");
  }

  function getSortDirection(col: DataTableColumn<T>): SortDirection {
    if (!sorting) return null;
    const field = col.sortField ?? col.id;
    if (sorting.field !== field) return null;
    return sorting.direction;
  }

  function withHighlight(node: ReactNode): ReactNode {
    if (!highlightQuery || typeof node !== "string") return node;
    return <HighlightMatch text={node} query={highlightQuery} />;
  }

  function renderCellValue(col: DataTableColumn<T>, row: T): ReactNode {
    if (col.cell) return col.cell(row);

    let value: unknown = undefined;
    if (col.getValue) value = col.getValue(row);
    else if (col.accessorKey) value = row[col.accessorKey];

    if (col.field) {
      return withHighlight(
        formatDisplayValue(col.field, value, resolvedFormatOptions),
      );
    }
    if (value == null || value === "") return "-";
    return withHighlight(String(value));
  }

  // ── Multi-select (reusable) ──────────────────────────────
  const selection = !!(selectable && selectedKeys && onSelectedKeysChange);
  const pageKeys = selection ? data.map((row, idx) => getRowKey(row, idx)) : [];
  const allSelected =
    selection && pageKeys.length > 0 && pageKeys.every((k) => selectedKeys!.has(k));
  const someSelected =
    selection && !allSelected && pageKeys.some((k) => selectedKeys!.has(k));
  const bodyColSpan = visibleColumns.length + (selection ? 1 : 0);

  function toggleAll() {
    if (!selection) return;
    const next = new Set(selectedKeys);
    if (allSelected) pageKeys.forEach((k) => next.delete(k));
    else pageKeys.forEach((k) => next.add(k));
    onSelectedKeysChange!(next);
  }
  function toggleRow(key: string) {
    if (!selection) return;
    const next = new Set(selectedKeys);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onSelectedKeysChange!(next);
  }

  const tableTestId = `table-${tableId}`;

  // A background refetch is in flight while stale rows are still shown. The
  // loading row already covers the "no rows yet" case, so this indeterminate
  // top-border line only surfaces when rows are painted and we're refreshing.
  const showRefetchBar = !!isFetching && !isLoading && data.length > 0;

  const content = (
    <div
      data-testid={tableTestId}
      className={cn(
        "cv-data-table",
        // Fullscreen renders through a portal to <body> (below), so `fixed
        // inset-0` is relative to the viewport and truly fills the screen even
        // when the table is nested inside a transformed container (e.g. a
        // centered Dialog, whose translate would otherwise trap `fixed`).
        // z-[100] sits above the Dialog overlay (z-50).
        isFullscreen &&
          "fixed inset-0 z-[100] flex flex-col overflow-hidden bg-[var(--bg)] p-4",
      )}
    >
      <div className={cn("relative", isFullscreen && "flex min-h-0 flex-1 flex-col")}>
        {/*
          Fullscreen toggle - top-right corner of the table. `absolute` so it
          overlays the header rule rather than adding a chrome row above the
          grid; ghost + backdrop so it stays legible over a header cell.
          z-30 keeps it above a right-pinned header cell (z-20).
        */}
        {!hideFullscreen && (
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            data-testid="data-table-fullscreen-toggle"
            aria-pressed={isFullscreen}
            onClick={() => setFullscreen((v) => !v)}
            title={
              isFullscreen
                ? t("common.exitFullScreen", "Exit full screen")
                : t("common.enterFullScreen", "Enter full screen")
            }
            className="absolute right-1.5 top-1.5 z-30 hidden rounded-[4px] bg-[var(--bg-light)] text-[var(--ink-3)] hover:text-[var(--ink)] md:inline-flex"
          >
            {isFullscreen ? (
              <Minimize2 className="h-3.5 w-3.5" />
            ) : (
              <Maximize2 className="h-3.5 w-3.5" />
            )}
            <span className="sr-only">
              {isFullscreen
                ? t("common.exitFullScreen", "Exit full screen")
                : t("common.enterFullScreen", "Enter full screen")}
            </span>
          </Button>
        )}
        {showRefetchBar && (
          <div
            className="cv-indeterminate-bar z-20"
            data-testid="data-table-refetch-bar"
          />
        )}
        <div
          ref={scrollRef}
          className={cn(
            "overflow-x-auto",
            !flush &&
              "rounded-lg border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)]",
            // Fullscreen: fill the remaining overlay height (the flex parent) and
            // scroll vertically inside it, so the pagination controls below stay
            // pinned and visible instead of being pushed past the viewport.
            isFullscreen && "min-h-0 flex-1 overflow-y-auto",
            // Bounded embedded surfaces (e.g. dashboard tiles): cap the height and
            // scroll the rows vertically, keeping the sticky header pinned. Ignored
            // in fullscreen (which owns the height). Only applied when the caller
            // opts in via maxHeightClass.
            !isFullscreen && maxHeightClass && `overflow-y-auto ${maxHeightClass}`,
            // Persistent, tasteful scrollbar wherever we scroll vertically -
            // macOS overlay scrollbars are invisible at rest, which made bounded
            // tables read as non-scrollable.
            (isFullscreen || maxHeightClass) && "cv-scroll",
            className,
          )}
        >
          <table className="w-full min-w-max">
          <thead>
            <tr className="border-b-[0.5px] border-b-[var(--rule-strong)]">
              {selection && (
                <th
                  className={cn(
                    "cv-data-table-compact sticky top-0 z-10 w-[44px] bg-[var(--bg)] [background-image:linear-gradient(var(--bg-light),var(--bg-light))] text-left",
                    headerPadClass,
                  )}
                >
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onCheckedChange={toggleAll}
                    aria-label="Select all rows"
                  />
                </th>
              )}
              {visibleColumns.map((col, colIndex) => {
                const isSortable = col.sortable && !!onSortingChange;
                const direction = getSortDirection(col);
                const isActive = direction !== null;
                const orderLabel = direction
                  ? sortOrderLabel(col.sortKind, direction)
                  : null;
                const isLastColumn = colIndex === visibleColumns.length - 1;

                return (
                  <th
                    key={col.id}
                    className={cn(
                      // Solid header: --bg-light alone is 98% opaque, so scrolled rows
                      // showed through. sticky is inert unless a caller constrains the height via
                      // `className`; it keeps the header in view when it does.
                      "cv-data-table-compact sticky top-0 z-10 bg-[var(--bg)] [background-image:linear-gradient(var(--bg-light),var(--bg-light))] font-normal uppercase tracking-[0.14em]",
                      col.align === "right" ? "text-right" : "text-left",
                      // A right-pinned column also needs a left hairline, so it
                      // reads as a fixed rail rather than as a column that has
                      // slid over the data behind it. Drawn as an inset shadow:
                      // a collapsed-table border stays on the grid and is lost
                      // under the pinned cell once the table scrolls.
                      col.sticky === "right" &&
                        "right-0 z-20 shadow-[inset_1px_0_0_var(--rule)]",
                      headerPadClass,
                      // Room for the full-screen toggle, which sits over the
                      // last header cell, so it never hides that sort arrow.
                      isLastColumn && !hideFullscreen && "pr-10",
                      isSortable &&
                        "group/sort cursor-pointer select-none hover:text-[var(--ink)]",
                      isActive ? "text-[var(--ink)]" : "text-[var(--ink-3)]",
                    )}
                    onClick={isSortable ? () => handleSort(col) : undefined}
                    title={isSortable ? sortHint(col, direction) : undefined}
                    aria-sort={
                      !isSortable
                        ? undefined
                        : direction === "asc"
                          ? "ascending"
                          : direction === "desc"
                            ? "descending"
                            : "none"
                    }
                  >
                    <span className="inline-flex items-center gap-1">
                      {col.header}
                      {isSortable && (
                        <SortIndicator
                          direction={direction}
                          active={isActive}
                        />
                      )}
                    </span>
                    {orderLabel && (
                      <span className="mt-0.5 block text-[11px] normal-case tracking-normal text-[var(--green)]">
                        {orderLabel}
                      </span>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {/*
              Show the loading row on the first fetch (`isLoading`) AND whenever
              a fetch is in flight but we have no rows to show yet
              (`isFetching && data.length === 0`). The second clause stops a
              stale/empty persisted cache from flashing the empty state while the
              real data is still loading. Rows already present are never masked.
            */}
            {isLoading || (isFetching && data.length === 0 && !isError) ? (
              <tr>
                <td
                  colSpan={bodyColSpan}
                  className="cv-data-table-body px-4 py-10 text-center text-[var(--ink-3)]"
                >
                  {t("common.loading")}
                </td>
              </tr>
            ) : isError && data.length === 0 ? (
              <tr>
                <td colSpan={bodyColSpan}>
                  <div className="cv-data-table-body flex flex-col items-center gap-3 px-4 py-10 text-center text-[var(--ink-3)]">
                    <AlertCircle className="h-5 w-5 text-[var(--ink-3)]" />
                    <span>
                      {t("common.loadError", {
                        defaultValue:
                          "Couldn't load this list. Please try again.",
                      })}
                    </span>
                    {onRetry && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={onRetry}
                        className="gap-1.5"
                      >
                        <RefreshCw className="h-3.5 w-3.5" />
                        {t("common.retry", { defaultValue: "Retry" })}
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={bodyColSpan}>
                  {emptyStateNode ?? (
                    <div className={emptyClass}>
                      {emptyMessage ?? t("common.noResults")}
                    </div>
                  )}
                </td>
              </tr>
            ) : renderRow ? (
              data.map((row) => renderRow(row, visibleColumns))
            ) : (
              data.map((row, idx) => {
                const detail = renderRowDetail?.(row, visibleColumns);
                return (
                  <Fragment key={getRowKey(row, idx)}>
                    <tr
                      data-testid={rowTestId?.(row)}
                      className={cn(
                        "border-b-[0.5px] border-b-[var(--rule)] transition-colors duration-100 hover:bg-[var(--bg-subtle)]",
                        detail == null && "last:border-b-0",
                        onRowClick && "cursor-pointer",
                      )}
                      onClick={onRowClick ? () => onRowClick(row) : undefined}
                    >
                      {selection && (
                        <td
                          className={cellClass}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Checkbox
                            checked={selectedKeys!.has(getRowKey(row, idx))}
                            onCheckedChange={() => toggleRow(getRowKey(row, idx))}
                            aria-label="Select row"
                          />
                        </td>
                      )}
                      {visibleColumns.map((col) => (
                        <td
                          key={col.id}
                          className={cn(
                            cellClass,
                            col.align === "right" && "text-right tabular-nums",
                            // Solid, like the header: --bg-light alone is 98%
                            // opaque, so the columns scrolling under it showed through.
                            col.sticky === "right" &&
                              "sticky right-0 z-10 bg-[var(--bg)] [background-image:linear-gradient(var(--bg-light),var(--bg-light))] shadow-[inset_1px_0_0_var(--rule)]",
                          )}
                        >
                          {renderCellValue(col, row)}
                        </td>
                      ))}
                    </tr>
                    {detail != null &&
                      (() => {
                        const lead = Math.max(
                          0,
                          Math.min(
                            renderRowDetailStartColumn ?? 0,
                            visibleColumns.length - 1,
                          ),
                        );
                        return (
                          <tr className="border-b-[0.5px] border-b-[var(--rule)] last:border-b-0">
                            {selection && (
                              <td
                                className={cn(
                                  "cv-data-table-body",
                                  highlightExpandedDetail &&
                                    "bg-[var(--bg-detail-highlight)]",
                                )}
                              />
                            )}
                            {Array.from({ length: lead }).map((_, i) => (
                              <td
                                key={`detail-lead-${i}`}
                                className={cn(
                                  "cv-data-table-body",
                                  highlightExpandedDetail &&
                                    "bg-[var(--bg-detail-highlight)]",
                                )}
                              />
                            ))}
                            <td
                              colSpan={visibleColumns.length - lead}
                              className={cn(
                                "cv-data-table-body px-4 py-2",
                                highlightExpandedDetail &&
                                  "bg-[var(--bg-detail-highlight)]",
                              )}
                            >
                              {fitRowDetailToViewport && viewportWidth ? (
                                // `sticky left-0` keeps the panel in view while
                                // the row above is scrolled sideways, so the
                                // detail stays readable instead of sliding away.
                                <div
                                  className="sticky left-0"
                                  style={{ width: viewportWidth - 32 }}
                                >
                                  {detail}
                                </div>
                              ) : (
                                detail
                              )}
                            </td>
                          </tr>
                        );
                      })()}
                  </Fragment>
                );
              })
            )}
          </tbody>
          </table>
        </div>
        <div
          aria-hidden="true"
          data-show={overflow.left}
          className="cv-scroll-fade cv-scroll-fade-left"
        />
        <div
          aria-hidden="true"
          data-show={overflow.right}
          className="cv-scroll-fade cv-scroll-fade-right"
        />
      </div>
      {page != null &&
        pageSize != null &&
        totalCount != null &&
        onPageChange &&
        onPageSizeChange && (
          <PaginationControls
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        )}
    </div>
  );

  // In fullscreen, portal to <body> so the overlay escapes any transformed
  // ancestor (Dialog centering) and covers the whole viewport.
  return isFullscreen && typeof document !== "undefined"
    ? createPortal(content, document.body)
    : content;
}
